<template>
    <div id="representative-settings" class="disableShortcut">
        <a-tabs :animated="false" default-active-key="1">
            <a-tab-pane key="1">
                <span slot="tab">
                    {{ $t("message.profile") }}
                </span>
                <label class="imgUpload" style="position: relative">
                    <a-avatar v-if="user.gravatar === null && gravatar === null" :size="64" icon="user" />
                    <a-avatar v-else :size="64" :src="gravatar || user.gravatar" />
                    <input id="profile-avatar" class="d-none" type="file" @change="uploadFile" />
                    <div class="imgUploadOver">+</div>
                </label>
                <div>
                    <div>{{ $t("message.email") }}</div>
                    <div>{{ user.email }}</div>
                </div>
                <md-field class="disableShortcut">
                    <label>{{ $t("message.username") }}</label>
                    <md-input v-model="user.name" class="disableShortcut"></md-input>
                </md-field>
                <a-button type="primary" @click="changeUserName">
                    {{ $t("message.updateUserName") }}
                </a-button>
            </a-tab-pane>
            <a-tab-pane key="2">
                <span slot="tab">
                    {{ $t("message.changePassword") }}
                </span>
                <md-field
                    :class="{
                        'md-invalid': errors.first($t('message.oldPassword')) != null,
                    }"
                    class="disableShortcut"
                >
                    <label>{{ $t("message.oldPassword", { num: "6" }) }}</label>
                    <md-input
                        :ref="$t('message.password')"
                        v-model="oldPassword"
                        v-validate="'required|min:6'"
                        :name="$t('message.oldPassword')"
                        :placeholder="$t('message.oldPassword', { num: '6' })"
                        autocomplete="off"
                        class="disableShortcut"
                        trim
                        type="password"
                    ></md-input>
                    <span class="md-error">{{ errors.first($t("message.oldPassword")) }}</span>
                </md-field>
                <md-field
                    :class="{
                        'md-invalid': errors.first($t('message.newPassword')) != null,
                    }"
                    class="disableShortcut"
                >
                    <label>{{ $t("message.newPassword", { num: "6" }) }}</label>
                    <md-input
                        :ref="$t('message.password')"
                        v-model="newPassword"
                        v-validate="'required|min:6'"
                        :name="$t('message.newPassword')"
                        :placeholder="$t('message.passwordAtLeast', { num: '6' })"
                        autocomplete="off"
                        class="disableShortcut"
                        trim
                        type="password"
                    ></md-input>
                    <span class="md-error">{{ errors.first($t("message.newPassword")) }}</span>
                </md-field>
                <md-field
                    :class="{
                        'md-invalid': errors.first($t('message.passwordRepeat')) != null,
                    }"
                    class="disableShortcut"
                >
                    <label>{{ $t("message.passwordRepeat") }}</label>
                    <md-input
                        v-model="passwordConfirmation"
                        v-validate="'confirmed:' + $t('message.password') + '|min:6'"
                        :name="$t('message.passwordRepeat')"
                        autocomplete="off"
                        class="disableShortcut"
                        type="password"
                    ></md-input>
                    <span class="md-error">{{ errors.first($t("message.passwordRepeat")) }}</span>
                </md-field>
                <a-button type="primary" @click="changePassword">
                    {{ $t("message.changePassword") }}
                </a-button>
            </a-tab-pane>
            <a-tab-pane key="3">
                <span slot="tab">
                    {{ $t("message.soundSettings") }}
                </span>
                <div class="sound-settings-container">
                    <div
                        v-for="(i, ii) in ['newVisitor', 'newConversation', 'newMessage', 'newPrivateMessage']"
                        class="sound-settings-element"
                    >
                        <div class="sound-settings-title">
                            {{ $t("message." + i) }}
                        </div>
                        <b-form-select
                            v-model="notificationSettings[ii].soundFile"
                            class="sound-settings-select"
                            @change="playPreviewSound(notificationSettings[ii])"
                        >
                            <b-form-select-option v-for="s in soundFiles" :key="s" :value="s"
                                >{{ s }}
                            </b-form-select-option>
                        </b-form-select>
                        <input
                            :id="i"
                            v-model="notificationSettings[ii].audioLevel"
                            max="100"
                            min="0"
                            :name="i"
                            step="10"
                            type="range"
                            @change="playPreviewSound(notificationSettings[ii])"
                        />
                        <label :for="i">{{ notificationSettings[ii].audioLevel }}%</label>
                        <md-button
                            class="md-icon-button md-primary play-sound-button"
                            @click="playPreviewSound(notificationSettings[ii])"
                        >
                            <md-icon>play_circle</md-icon>
                        </md-button>
                    </div>
                </div>
                <div class="desktop-notifications">
                    <p class="desktop-notifications-title">
                        {{ $t("message.desktopNotifications") }}
                    </p>
                    <div class="desktop-notifications-switches">
                        <div v-for="(i, ii) in ['newVisitor', 'newConversation', 'newMessage', 'newPrivateMessage']">
                            <span>{{ $t("message." + i) }}</span>
                            <md-switch
                                v-model="notificationSettings[ii].desktopNotification"
                                class="md-primary"
                                @change="(e) => notificationRequest(e, i)"
                            />
                        </div>
                    </div>
                </div>
                <div class="notification-warning">
                    <img alt="warning" class="notification-warning-img" src="https://static.supsis.live/secure.png" />
                    <p class="notification-warning-text">
                        {{ $t("message.problemsSound") }}
                    </p>
                </div>
            </a-tab-pane>
            <a-tab-pane key="4">
                <span slot="tab">
                    {{ $t("message.languageSettings") }}
                </span>
                <select v-model="lang">
                    <option v-for="lang in languages" :value="lang.code">{{ lang.originalName }}</option>
                </select>
            </a-tab-pane>
        </a-tabs>
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { sendSocketMessage } from "../utils/socketStream";
import * as _ from "lodash";
import log2 from "../utils/log2";
import { enableDesktopNotification } from "../utils/desktopNotification";
import { uploadAsset } from "../api/assetUpload";
import axios from "axios";
import languages from "../language/languages.json";

const log = log2("RepresentativeSettings");
export default {
    name: "RepresentativeSettings",
    data() {
        return {
            languages,
            gravatar: null,
            isFirstOpening: true,
            oldPassword: "",
            newPassword: "",
            passwordConfirmation: "",
            volume: 0,
            notificationSettings: [
                {
                    type: "new_visitor",
                    soundFile: "new-conversation-default",
                    audioLevel: 100,
                    desktopNotification: false,
                },
                {
                    type: "new_conversation",
                    soundFile: "for_sure",
                    audioLevel: 100,
                    desktopNotification: false,
                },
                {
                    type: "new_message",
                    soundFile: "for_sure",
                    audioLevel: 100,
                    desktopNotification: false,
                },
                {
                    type: "private_message",
                    soundFile: "for_sure",
                    audioLevel: 100,
                    desktopNotification: false,
                },
            ],
            soundFiles: [
                "done-for-you",
                "exquisite",
                "for-sure",
                "hey-champ",
                "juntos",
                "just-saying",
                "oringz",
                "percussion-sound",
                "plucky",
                "sharp",
                "swiftly",
                "take-this",
                "pm-default",
                "new-message-default",
                "new-conversation-default",
                "presto",
                "telefono-viejo",
            ],
        };
    },
    computed: {
        ...mapState({
            user: (state) => _.cloneDeep(state.auth.user),
            site: (state) => state.site.site,
            isAdmin: (state) => state.auth.adminUser != null,
        }),
        lang: {
            get() {
                return this.user.lang;
            },
            set(lang) {
                this.updateUser({ _id: this.user._id, lang }).then(() => {
                    this.updateLang(lang);
                    sendSocketMessage("user lang-change", {
                        userId: this.user._id,
                        siteId: this.site._id,
                        lang: lang,
                    });
                });
            },
        },
    },
    methods: {
        ...mapActions({
            getUsers: "user/getUsers",
            getUser: "user/getUser",
            updateUser: "user/updateUser",
            updateUserPassword: "user/updateUserPassword",
            refreshAuthUser: "auth/refreshAuthUser",
        }),
        ...mapMutations({
            updateLang: "auth/UPDATE_LANG",
        }),
        notificationRequest(isEnabled, index) {
            log("is enabled", isEnabled);
            if (isEnabled && Notification.permission !== "granted") {
                enableDesktopNotification()
                    .then((x) => {
                        log("notification granted");
                    })
                    .catch((x) => {
                        log("notification not granted");
                        this.notificationSettings[index].desktopNotification = false;
                    });
            }
        },
        playPreviewSound(settings) {
            log("play sound is here ", settings);
            let audio = new Audio(`./static/media/ogg_folder/${settings.soundFile}.ogg`);
            audio.volume = settings.audioLevel / 100;
            audio.play();
        },
        updateNotificationSettings: _.debounce(async function (notificationSettings) {
            this.user.notificationSettings = notificationSettings;
            await this.updateUser({ ...this.user });
            await this.refreshAuthUser();
            await this.$toasted.show(this.$t("message.userVolumeUpdated"), {
                duration: 3000,
                position: "bottom-center",
            });
        }, 500),

        changeUserName() {
            this.$validator.validateAll().then((response) => {
                if (response) {
                    const result = this.updateUser({ ...this.user });
                    result.then(() => {
                        this.$toasted.show(this.$t("message.userSaved"), {
                            duration: 3000,
                            position: "bottom-center",
                        });
                        const socketMessageId = this.isAdmin ? "admin user-updated" : "user user-updated";
                        sendSocketMessage(socketMessageId, {
                            userId: this.user._id,
                            siteId: this.site._id,
                        });
                    });
                }
            });
        },
        changePassword() {
            this.$validator.validateAll().then((response) => {
                if (response) {
                    this.updateUserPassword({
                        id: this.user._id,
                        oldPassword: this.oldPassword,
                        newPassword: this.newPassword,
                    })
                        .then(() => {
                            this.oldPassword = "";
                            this.newPassword = "";
                            this.passwordConfirmation = "";
                            //clear errors when text empty and password change success
                            this.$nextTick(() => {
                                this.errors.clear();
                                this.$nextTick(() => {
                                    this.$validator.reset();
                                });
                            });
                            this.$toasted.show(this.$t("message.passwordChangeSuccess"), {
                                duration: 5000,
                                position: "bottom-center",
                            });
                        })
                        .catch(() => {
                            this.$toasted.show(this.$t("message.oldPasswordError"), {
                                duration: 5000,
                                position: "bottom-center",
                            });
                        });
                }
            });
        },
        beforeUpload(file) {
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png";
            if (!isJpgOrPng) {
                this.$toasted.show("Sadece Resim Yükleyebilirsiniz!", {
                    duration: 3000,
                });
            }
            const isLt2M = file.size / 1024 / 1024 < 20;
            if (!isLt2M) {
                this.$toasted.show("Resminizin Boyutu En Fazla 2MB Olmalıdır!", {
                    duration: 3000,
                });
            }
            return isJpgOrPng && isLt2M;
        },
        async uploadFile(e) {
            let {
                files: [file],
            } = document.getElementById(e.target.id);
            if (!this.beforeUpload(file)) return;
            let path = `${this.site._id}/avatar/${this.user._id}`;
            try {
                let imdUploadData = await uploadAsset({ path, mime: file.type });
                this.updateUser({
                    ...this.user,
                    gravatar: `https://static.supsis.live/${path + imdUploadData.data.asset._id}`,
                });

                await axios({
                    method: imdUploadData.data.method,
                    url: imdUploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
                this.gravatar = `https://static.supsis.live/${path + imdUploadData.data.asset._id}`;
            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            }
        },
    },
    mounted() {
        if (this.user) {
            this.notificationSettings = this.notificationSettings.map((x) => {
                let md = this.user.notificationSettings.find((xx) => xx.type === x.type);
                if (!!md) return md;
                return x;
            });
        }
    },
    watch: {
        notificationSettings: {
            handler(newValue) {
                if (newValue) {
                    if (this.isFirstOpening) {
                        this.isFirstOpening = false;
                    } else {
                        this.updateNotificationSettings(newValue);
                    }
                }
            },
            deep: true,
        },
        user(newUser) {
            log.debug("newUser", newUser);
            this.volume = newUser.audioAlertVolume ? newUser.audioAlertVolume * 100 : 0;
        },
    },
    beforeDestroy() {
        this.isFirstOpening = true;
    },
};
</script>

<style lang="scss" scoped>
.sound-settings-container {
    display: flex;
    flex-direction: column;

    .sound-settings-element {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .sound-settings-select {
            width: 140px;
        }

        .sound-settings-title {
            width: 20%;
        }

        label {
            margin-right: 2px !important;
            width: 10%;
        }

        .play-sound-button {
            margin-right: 2px !important;
            margin-left: 2px !important;
            margin-bottom: 12px !important;
            padding: 2px !important;
        }
    }

    .sound-settings-element > *:not(:last-child) {
        display: block;
        margin-right: 10px;
        margin-bottom: 12px;
    }
}

.desktop-notifications {
    margin-top: 6px;

    &-title {
        margin-bottom: 0;
        font-weight: 500;
    }

    &-switches {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
}

.notification-warning {
    display: flex;
    align-items: center;
    margin-top: 6px;

    &-img {
        margin-right: 12px;
        transform: translateY(-35%);
    }

    &-text {
        font-family: Poppins, serif;
    }
}

.imgUpload {
    cursor: pointer;

    &:hover {
        .imgUploadOver {
            opacity: 1;
        }
    }

    .imgUploadOver {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
        background-color: rgba(0, 0, 0, 0.55);
        color: #ffffff;
        font-size: 32px;
        opacity: 0;
        transition: all 0.3s ease-in-out;
        cursor: pointer;
        border-radius: 100%;
        text-align: center;
        line-height: 64px;
    }
}
</style>
